import clsx from 'clsx';

export interface CounterQuadProps {
  total: number;
  user: number;
  arcanum: number;
  derived: number;
  className?: string;
}

interface QuadItem {
  label: string;
  value: number;
  testId: string;
}

/**
 * 4-stat header strip for the `<VariablesPanel>` (Lovable pull-in,
 * Phase 3 Session 3). Per-counter typography is fixed at the prototype's
 * exact pixel sizes — `text-[20px]` mono accent for the number,
 * `text-[7px]` mono dim uppercase for the label. The 7px font-size relies
 * on Tailwind v4's arbitrary-value escape — no built-in utility maps to
 * that step.
 *
 * Counts are derived in the parent (per-bucket lengths). The component is
 * presentational only — no filtering or reactivity logic here.
 */
export function CounterQuad({
  total,
  user,
  arcanum,
  derived,
  className,
}: CounterQuadProps) {
  const items: QuadItem[] = [
    // "EDITABLE" = total addressable inputs (scalar inputs + table cells +
    // location defs) that can be edited by the user or Arcanum. Renamed from
    // "TRACKED" in builder-ux-overhaul WS2 (ADR 0005, 2026-05-22) to reflect
    // that this counter represents the EDITABLE universe, not merely a tracked
    // count. variables-counter-coherence Inc-3 (2026-05-20) aligned this value
    // with MODEL OVERVIEW's denominator so both counters report the same total
    // (was the pre-Inc-3 "43 vs 68" split). data-testid="counter-total"
    // PRESERVED (label ≠ testId precedent — YOUR/counter-user,
    // ARCANUM/counter-arcanum already diverge in this same file).
    { label: 'EDITABLE', value: total, testId: 'counter-total' },
    { label: 'YOUR', value: user, testId: 'counter-user' },
    { label: 'ARCANUM', value: arcanum, testId: 'counter-arcanum' },
    { label: 'DERIVED', value: derived, testId: 'counter-derived' },
  ];

  return (
    <div
      data-testid="counter-quad"
      className={clsx('grid grid-cols-4 gap-2', className)}
    >
      {items.map(({ label, value, testId }) => (
        <div
          key={label}
          data-testid={testId}
          className="flex flex-col items-start"
        >
          <span className="text-[20px] font-mono text-accent tabular-nums leading-none">
            {value}
          </span>
          <span className="text-[7px] font-mono text-text-dim uppercase tracking-[0.4px] mt-0.5">
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}
